<script lang="ts">
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { editable } from '$lib/storyblok/editable';
	import type { SbImage, SbLink } from '$lib/storyblok/types';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';

	interface Props {
		content: ISbStoryData<{
			headline: ISbRichtext;
			subheadline: ISbRichtext;
			cta_label: string;
			cta_logo: SbImage;
			cta_link: SbLink;
			large_intro: string;
			large_headline: string;
			large_value: string;
			large_type: string;
			large_image: ISbStoryData[];
			large_background_texture: SbImage;
			small_intro: string;
			small_headline: string;
			small_value: string;
			small_type: string;
			small_background_texture: SbImage;
		}>['content'];
	}

	let { content }: Props = $props();
</script>

<section class={['bg-white text-brown-1 lg:mb-10']} use:editable={content}>
	<PageGrid16 margin>
		<div
			class="payt-headline col-span-full text-balance text-[36px]/[122%] tracking-[-.36px] lg:col-start-3 lg:col-end-9 lg:text-[48px]/[122%] lg:tracking-[-.48px]"
		>
			<SbRichtext text={content.headline} />
		</div>
		<div class="col-span-full max-lg:mt-5 lg:col-start-9 lg:col-end-[15]">
			<div class="text-[14px]/[160%]">
				<SbRichtext text={content.subheadline} />
			</div>
			<div class="mt-5 flex lg:mt-9">
				<a
					href={getSbUrl(content.cta_link)}
					class="flex items-center gap-x-1 rounded-xl border border-brown-5 p-2 text-[12px] transition-colors hover:bg-brown-6"
				>
					<span class="mr-1 h-8 w-8 rounded-full border border-brown-5 p-2">
						<Image
							src={content.cta_logo.filename}
							alt={content.cta_logo.alt}
							title={content.cta_logo.title}
							class="h-full w-full"
							height={28}
							width={28}
						/>
					</span>
					{content.cta_label}
					<ArrowRight class="h-4 w-4" />
				</a>
			</div>
		</div>

		<div class="col-span-full mt-5 max-lg:overflow-hidden lg:col-start-3 lg:col-end-8 lg:mt-22">
			<div
				class="relative flex h-[421px] w-full flex-col justify-between rounded-xl bg-brown-6 p-6 pb-7"
			>
				<div class="z-20">
					<p class=" text-[14px]/[140%] font-semibold uppercase tracking-[1.12px] text-brown-4">
						{content.large_intro}
					</p>
					<p
						class="mt-2 text-[20px]/[140%] tracking-[-.2px] lg:text-[24px]/[140%] lg:tracking-[-.24px]"
					>
						{content.large_headline}
					</p>
				</div>

				<p class="z-20 flex items-end gap-x-1">
					<span class="font-rhymes text-[52px]/[100%] font-light lg:text-[64px]/[100%]">
						{content.large_value}
					</span>
					<span class="text-[24px]/[100%] tracking-[-.24px]">
						{content.large_type}
					</span>
				</p>

				{#if content.large_image[0]}
					<div
						class="absolute -bottom-1 -right-9 z-10 min-h-20 w-[200px] lg:-bottom-7 lg:-right-10"
					>
						<Blok content={content.large_image[0]} />
					</div>
				{/if}

				{#if content.large_background_texture.filename}
					<Image
						src={content.large_background_texture.filename}
						wrapperClass="absolute inset-0"
						class="h-full w-full rounded-xl object-cover"
					/>
				{/if}
			</div>
		</div>

		<div class="col-span-full flex flex-col justify-end max-lg:mt-5 lg:col-start-9 lg:col-end-[14]">
			<div
				class="relative flex h-[212px] w-full flex-col justify-between rounded-xl bg-green-3 p-6 pb-7 text-white"
			>
				<div class="z-20">
					<p class="text-[14px]/[140%] font-semibold uppercase tracking-[1.12px] text-green-6">
						{content.small_intro}
					</p>
					<p
						class="mt-2 text-[20px]/[140%] tracking-[-.2px] lg:text-[24px]/[140%] lg:tracking-[-.24px]"
					>
						{content.small_headline}
					</p>
				</div>

				<p class="z-20 flex items-end gap-x-1">
					<span class="font-rhymes text-[52px]/[100%] font-light lg:text-[64px]/[100%]">
						{content.small_value}
					</span>
					<span class="text-[24px]/[100%] tracking-[-.24px]">
						{content.small_type}
					</span>
				</p>

				{#if content.small_background_texture.filename}
					<Image
						src={content.small_background_texture.filename}
						wrapperClass="absolute inset-0"
						class="h-full w-full rounded-xl object-cover"
					/>
				{/if}
			</div>
		</div>
	</PageGrid16>
</section>

<style>
	:global(.payt-headline .highlight) {
		background-color: theme('colors.green.6');
		color: theme('colors.green.3');
		border-radius: 8px;
		padding-left: 8px;
		padding-right: 8px;
		white-space: nowrap;
		letter-spacing: -1.44px;
		font-family: theme('fontFamily.rhymes');
		display: inline-block;
		padding-bottom: 4px;
		margin-top: 4px;
	}
</style>
